/* eslint-disable max-len */

const GOLDCAST_STUDIO_NAME = 'Cvent | Splash Studio';
export const GOLDCAST_SUPPORT_URL = 'https://support.splashthat.com/hc/en-us';
const GOLDCAST_ERROR_MESSAGE_PATTERN = /goldcast|cvent\s*(\|\s*)?splash studio/i;

const GOLDCAST_INTEGRATION_NOT_ENABLED = 'goldcast_integration_not_enabled';
const GOLDCAST_ORG_CONFIG_MISSING = 'goldcast_org_config_missing';
const MISSING_START_DATE = 'missing_start_date';
const MISSING_END_DATE = 'missing_end_date';
const MISSING_TIMEZONE = 'missing_timezone';
const EVENT_NOT_IN_FUTURE = 'event_not_in_future';
const INVALID_DATE_ORDERING = 'invalid_date_ordering';
const INVALID_DURATION = 'invalid_duration';
const EVENT_SPANS_MULTIPLE_DAYS = 'event_spans_multiple_days';

const GOLDCAST_ELIGIBILITY_REASON_COPY = {
    [GOLDCAST_INTEGRATION_NOT_ENABLED]: {
        validationLabel: 'Integration setup is required',
        validationAction: 'enable the integration in your Integrations tab',
        tooltipAction: 'enable the integration in your Integrations tab',
    },
    [GOLDCAST_ORG_CONFIG_MISSING]: {
        validationLabel: 'Organization configuration is required',
        validationAction: 'contact support',
        tooltipAction: 'contact support',
    },
    [MISSING_START_DATE]: {
        validationLabel: 'Start time is required',
        validationAction: 'adjust your start time',
        tooltipAction: 'set a start time in the event settings',
    },
    [MISSING_END_DATE]: {
        validationLabel: 'End time is required',
        validationAction: 'adjust your end time',
        tooltipAction: 'set an end time in the event settings',
    },
    [MISSING_TIMEZONE]: {
        validationLabel: 'Timezone is required',
        validationAction: 'adjust your timezone',
        tooltipAction: 'set a timezone in the event settings',
    },
    [EVENT_NOT_IN_FUTURE]: {
        validationLabel: 'Start time must be in the future',
        validationAction: 'adjust your start time',
        tooltipAction: 'set a future start time in the event settings',
    },
    [INVALID_DATE_ORDERING]: {
        validationLabel: 'Start time must be before end time',
        validationAction: 'adjust your event timing',
        tooltipAction: 'set the start time before the end time in the event settings',
    },
    [INVALID_DURATION]: {
        validationLabel: 'Event duration must be at least 1 minute',
        validationAction: 'adjust your event duration',
        tooltipAction: 'set an event duration of at least 1 minute in the event settings',
    },
    [EVENT_SPANS_MULTIPLE_DAYS]: {
        validationLabel: 'Event must start and end on the same day',
        validationAction: 'adjust your event date',
        tooltipAction: 'set start and end dates on the same day in the event settings',
    },
};

const GOLDCAST_ELIGIBILITY_REASONS = Object.keys(GOLDCAST_ELIGIBILITY_REASON_COPY);

const GOLDCAST_GENERIC_DATE_TIME_ELIGIBILITY_MESSAGE = `The current event settings conflict with ${GOLDCAST_STUDIO_NAME}. Switch your embed type or adjust your event settings to save this change.`;

const GOLDCAST_EMBED_DISABLED_TOOLTIP = `${GOLDCAST_STUDIO_NAME} only supports single-day, non-multi-session events. Kindly set start and end dates in the event settings to access it.`;

const DATE_SESSION_ELIGIBILITY_REASONS = new Set([
    MISSING_START_DATE,
    MISSING_END_DATE,
    MISSING_TIMEZONE,
    EVENT_NOT_IN_FUTURE,
    INVALID_DATE_ORDERING,
    INVALID_DURATION,
    EVENT_SPANS_MULTIPLE_DAYS,
]);

const findReasonInString = (value) =>
    typeof value === 'string' ? GOLDCAST_ELIGIBILITY_REASONS.find((reason) => value.includes(reason)) || null : null;

const getGoldcastDateTimeEligibilityMessage = (reason) => {
    const { validationLabel, validationAction } = GOLDCAST_ELIGIBILITY_REASON_COPY[reason] ?? {};

    return validationLabel
        ? `${validationLabel} for ${GOLDCAST_STUDIO_NAME} embed option. Switch your embed type or ${validationAction} to save this change.`
        : GOLDCAST_GENERIC_DATE_TIME_ELIGIBILITY_MESSAGE;
};

const getTooltipPrefix = (reason) =>
    DATE_SESSION_ELIGIBILITY_REASONS.has(reason)
        ? `${GOLDCAST_STUDIO_NAME} only supports single-day, non-multi-session events.`
        : `${GOLDCAST_STUDIO_NAME} is not currently available.`;

const getTooltipMessage = (reason) => {
    const tooltipAction = GOLDCAST_ELIGIBILITY_REASON_COPY[reason]?.tooltipAction;

    return tooltipAction
        ? `${getTooltipPrefix(reason)} Kindly ${tooltipAction} to access it.`
        : GOLDCAST_EMBED_DISABLED_TOOLTIP;
};

export const getGoldcastDateTimeEligibilityErrorMessage = (message) => {
    if (typeof message !== 'string' || !GOLDCAST_ERROR_MESSAGE_PATTERN.test(message)) {
        return null;
    }

    return getGoldcastDateTimeEligibilityMessage(findReasonInString(message));
};

export const getGoldcastEmbedDisabledTooltip = (reason) => getTooltipMessage(reason);
